import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { 
  Loader2, Lock, ExternalLink, Download, Calendar, 
  Trophy, Target, Zap, Star, Crown, ChevronRight,
  User, Mail, Phone, Play, CheckCircle, Circle, Sparkles
} from 'lucide-react';
import ConsultationScheduler from '../components/ConsultationScheduler';
import AcademyCalendar from '../components/AcademyCalendar';
import api from '../services/api';
import { toast } from 'sonner';

const TIER_CONFIG = {
  baseline: {
    name: 'Baseline',
    icon: Zap,
    color: '#60A5FA',
    badge: 'BASELINE'
  },
  academy_major: {
    name: 'Academy Major',
    icon: Star,
    color: '#F97316',
    badge: 'MAJOR'
  },
  elite_pivot: {
    name: 'Elite Pivot',
    icon: Crown,
    color: '#D4AF37',
    badge: 'ELITE'
  }
};

const ZONE_CONFIG = {
  clogging: { label: 'Clogging Zone', color: '#EF4444' },
  coasting: { label: 'Coasting Zone', color: '#F59E0B' },
  elite: { label: 'Elite Zone', color: '#D4AF37' }
};

// Onboarding Checklist Component
const OnboardingChecklist = ({ progress, onUpdate, studentName, onWatchWelcomeFilm }) => {
  const items = [
    {
      id: 'welcome_film',
      title: 'Watch the Welcome Film',
      description: 'See your Signing Day celebration',
      icon: Play,
      action: 'modal', // Changed from link to modal
      linkType: 'modal'
    },
    {
      id: 'ixl_combine',
      title: 'Enter the Combine',
      description: 'Complete your IXL baseline diagnostic',
      icon: Target,
      link: 'https://www.ixl.com/signin/prsvracademy',
      linkType: 'external'
    },
    {
      id: 'day2_playbook',
      title: 'The First Pivot',
      description: 'Download the Day 2 Playbook PDF',
      icon: Download,
      link: 'https://customer-assets.emergentagent.com/job_scholar-sprint-hub/artifacts/su5i7kvp_30%20Day%20Roadmap.pdf.pdf',
      linkType: 'download'
    }
  ];

  const completedCount = items.filter(item => progress[item.id]?.completed).length;
  const allCompleted = completedCount === items.length;

  const handleToggle = async (itemId) => {
    const currentStatus = progress[itemId]?.completed || false;
    onUpdate(itemId, !currentStatus);
  };

  return (
    <Card className="bg-gradient-to-br from-gray-900 to-black border-2 border-orange-600/30 mb-8 overflow-hidden">
      <div className="absolute top-0 right-0 w-64 h-64 bg-orange-600/5 rounded-full -mr-32 -mt-32 blur-3xl"></div>
      
      <CardHeader className="relative">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-xl font-black text-white flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-orange-600" />
              Pre-Launch Checklist
            </CardTitle>
            <p className="text-gray-500 text-sm mt-1">Complete these before January 8th</p>
          </div>
          <div className="text-right">
            <div className="text-3xl font-black text-orange-600">{completedCount}/{items.length}</div>
            <div className="text-gray-500 text-xs uppercase tracking-wider">Complete</div>
          </div>
        </div>
        
        {/* Progress bar */}
        <div className="mt-4 h-2 bg-gray-800 rounded-full overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-orange-600 to-orange-500 transition-all duration-500"
            style={{ width: `${(completedCount / items.length) * 100}%` }}
          />
        </div>
      </CardHeader>
      
      <CardContent className="relative">
        <div className="space-y-3">
          {items.map((item, idx) => {
            const isCompleted = progress[item.id]?.completed;
            const Icon = item.icon;
            
            return (
              <div 
                key={item.id}
                className={`flex items-center gap-4 p-4 rounded-lg border transition-all ${
                  isCompleted 
                    ? 'bg-green-600/10 border-green-600/30' 
                    : 'bg-white/5 border-gray-800 hover:border-orange-600/30'
                }`}
              >
                {/* Checkbox */}
                <button
                  onClick={() => handleToggle(item.id)}
                  className={`w-8 h-8 rounded-full border-2 flex items-center justify-center transition-all flex-shrink-0 ${
                    isCompleted
                      ? 'bg-green-600 border-green-600'
                      : 'border-gray-600 hover:border-orange-600'
                  }`}
                >
                  {isCompleted ? (
                    <CheckCircle className="w-5 h-5 text-white" />
                  ) : (
                    <span className="text-gray-600 font-bold">{idx + 1}</span>
                  )}
                </button>
                
                {/* Content */}
                <div className="flex-1">
                  <div className={`font-bold ${isCompleted ? 'text-green-500 line-through' : 'text-white'}`}>
                    {item.title}
                  </div>
                  <div className="text-gray-500 text-sm">{item.description}</div>
                </div>
                
                {/* Action */}
                {item.linkType === 'modal' ? (
                  <button 
                    onClick={onWatchWelcomeFilm}
                    className="p-2 rounded-lg bg-orange-600/20 text-orange-600 hover:bg-orange-600 hover:text-white transition-all"
                  >
                    <Icon className="w-5 h-5" />
                  </button>
                ) : item.linkType === 'external' ? (
                  <a 
                    href={item.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 rounded-lg bg-orange-600/20 text-orange-600 hover:bg-orange-600 hover:text-white transition-all"
                  >
                    <Icon className="w-5 h-5" />
                  </a>
                ) : (
                  <a 
                    href={item.link}
                    download
                    className="p-2 rounded-lg bg-orange-600/20 text-orange-600 hover:bg-orange-600 hover:text-white transition-all"
                  >
                    <Icon className="w-5 h-5" />
                  </a>
                )}
              </div>
            );
          })}
        </div>
        
        {/* Completion Message */}
        {allCompleted && (
          <div className="mt-6 p-4 bg-green-600/20 border border-green-600/30 rounded-lg text-center">
            <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2" />
            <div className="text-green-500 font-bold text-lg">You are cleared for the Jan 8th Kickoff!</div>
            <div className="text-gray-400 text-sm mt-1">Stay tuned for your Week 1 modules.</div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const AcademyDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [showScheduler, setShowScheduler] = useState(false);
  const [onboardingProgress, setOnboardingProgress] = useState({});
  const [showWelcomeFilm, setShowWelcomeFilm] = useState(false);

  useEffect(() => {
    checkAuthAndLoadDashboard();
  }, []);

  const checkAuthAndLoadDashboard = async () => {
    const token = localStorage.getItem('academy_token');
    
    if (!token) {
      toast.error('Please log in to access the dashboard');
      navigate('/academy/enroll');
      return;
    }

    try {
      const response = await api.get('/academy/dashboard', {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setDashboardData(response.data);
        // Set onboarding progress from user data
        setOnboardingProgress(response.data.student?.onboarding_progress || {});
      }
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Please complete enrollment to access the dashboard');
        navigate('/academy/enroll');
      } else if (error.response?.status === 401) {
        localStorage.removeItem('academy_token');
        localStorage.removeItem('academy_user');
        toast.error('Session expired. Please log in again.');
        navigate('/academy/enroll');
      } else {
        toast.error('Failed to load dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  const updateOnboardingProgress = async (itemId, completed) => {
    try {
      const token = localStorage.getItem('academy_token');
      const response = await api.put('/academy/onboarding/progress', 
        { item: itemId, completed },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      if (response.data.success) {
        setOnboardingProgress(response.data.onboarding_progress);
        if (response.data.all_completed) {
          toast.success('🎉 All pre-launch tasks complete!');
        }
      }
    } catch (error) {
      toast.error('Failed to update progress');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('academy_token');
    localStorage.removeItem('academy_user');
    navigate('/academy/enroll');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white pt-16 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-orange-600 mx-auto mb-4" />
          <p className="text-gray-400">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return null;
  }

  const { student, tier_info, modules, links, welcome_message } = dashboardData;
  const tierConfig = TIER_CONFIG[student.subscription_tier] || TIER_CONFIG.baseline;
  const zoneConfig = ZONE_CONFIG[student.readiness_zone] || ZONE_CONFIG.elite;
  const TierIcon = tierConfig.icon;
  const isElite = student.subscription_tier === 'elite_pivot';

  return (
    <div className="min-h-screen bg-black text-white pt-16">
      <div className="container mx-auto px-4 sm:px-6 py-4 sm:py-8">
        {/* Header with Profile - Mobile Responsive */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 sm:gap-6 mb-6 sm:mb-12">
          <div className="flex items-center gap-3 sm:gap-6">
            {/* Avatar - Smaller on mobile */}
            <div 
              className="w-12 h-12 sm:w-20 sm:h-20 rounded-full flex items-center justify-center text-xl sm:text-3xl font-black flex-shrink-0"
              style={{ backgroundColor: `${tierConfig.color}20`, border: `3px solid ${tierConfig.color}` }}
            >
              {student.name.charAt(0).toUpperCase()}
            </div>
            
            <div>
              <h1 className="text-xl sm:text-3xl font-black text-white">{student.name}</h1>
              <div className="flex flex-wrap gap-2 sm:gap-3 mt-1 sm:mt-2">
                {/* Tier Badge */}
                <span 
                  className="px-3 py-1 text-xs font-bold uppercase tracking-wider flex items-center gap-1"
                  style={{ backgroundColor: `${tierConfig.color}20`, color: tierConfig.color, border: `1px solid ${tierConfig.color}` }}
                >
                  <TierIcon className="w-3 h-3" />
                  {tierConfig.badge}
                </span>
                
                {/* Zone Badge */}
                {student.readiness_zone && (
                  <span 
                    className="px-3 py-1 text-xs font-bold uppercase tracking-wider"
                    style={{ backgroundColor: `${zoneConfig.color}20`, color: zoneConfig.color, border: `1px solid ${zoneConfig.color}` }}
                  >
                    {zoneConfig.label}
                  </span>
                )}
              </div>
            </div>
          </div>

          <Button 
            onClick={handleLogout}
            variant="outline"
            className="border-gray-700 text-gray-400 hover:text-white hover:border-gray-500"
          >
            Log Out
          </Button>
        </div>

        {/* Onboarding Checklist - Show before Jan 8th */}
        <OnboardingChecklist 
          progress={onboardingProgress}
          onUpdate={updateOnboardingProgress}
          studentName={student.name}
          onWatchWelcomeFilm={() => setShowWelcomeFilm(true)}
        />

        {/* Academy Calendar — highlights field trips, holidays, deadlines */}
        <div className="mb-6 sm:mb-8">
          <AcademyCalendar gradeFilter={student.grade || null} title="School Calendar" />
        </div>

        {/* Welcome Message */}
        <Card className="bg-gradient-to-r from-orange-600/20 to-transparent border-orange-600/30 mb-8">
          <CardContent className="py-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex items-start gap-4">
                <Trophy className="w-8 h-8 text-orange-600 flex-shrink-0" />
                <div>
                  <h3 className="text-lg font-bold text-white mb-1">Welcome to PRSVR Academy</h3>
                  <p className="text-gray-300">{welcome_message}</p>
                </div>
              </div>
              <Link to="/academy/lms">
                <Button className="bg-orange-600 hover:bg-orange-700 text-white font-bold px-6">
                  Enter Training Ground
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {/* IXL Combine */}
          <Card className="bg-gray-900 border-gray-800 hover:border-orange-600/50 transition-all cursor-pointer group">
            <a href={links.ixl_combine} target="_blank" rel="noopener noreferrer">
              <CardContent className="py-8 text-center">
                <div className="w-16 h-16 rounded-full bg-orange-600/20 flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                  <Target className="w-8 h-8 text-orange-600" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Launch IXL Combine</h3>
                <p className="text-gray-400 text-sm mb-2">Begin your baseline diagnostic</p>
                {/* SmartScore Sync Status */}
                <div className="text-xs text-gray-500 mb-4 flex items-center justify-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-yellow-500 animate-pulse"></div>
                  Current SmartScore: <span className="text-yellow-500">Pending Sync</span>
                </div>
                <span className="text-orange-600 text-sm font-bold flex items-center justify-center gap-1">
                  Open Combine <ExternalLink className="w-4 h-4" />
                </span>
              </CardContent>
            </a>
          </Card>

          {/* Week 1 Prep Download */}
          <Card className="bg-gray-900 border-gray-800 hover:border-blue-600/50 transition-all cursor-pointer group">
            <a href={links.week1_prep_pdf} download>
              <CardContent className="py-8 text-center">
                <div className="w-16 h-16 rounded-full bg-blue-600/20 flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                  <Download className="w-8 h-8 text-blue-500" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Week 1 Prep</h3>
                <p className="text-gray-400 text-sm mb-4">Download your Pace & Pivot PDF</p>
                <span className="text-blue-500 text-sm font-bold flex items-center justify-center gap-1">
                  Download PDF <Download className="w-4 h-4" />
                </span>
              </CardContent>
            </a>
          </Card>

          {/* Elite Only: Schedule 1-on-1 */}
          {isElite ? (
            <Card 
              className="bg-gray-900 border-gray-800 hover:border-amber-500/50 transition-all cursor-pointer group"
              onClick={() => setShowScheduler(true)}
            >
              <CardContent className="py-8 text-center">
                <div className="w-16 h-16 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                  <Calendar className="w-8 h-8 text-amber-500" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Schedule 1-on-1</h3>
                <p className="text-gray-400 text-sm mb-4">Book your weekly strategy call</p>
                <span className="text-amber-500 text-sm font-bold flex items-center justify-center gap-1">
                  Book Now <ChevronRight className="w-4 h-4" />
                </span>
                <div className="mt-2 px-2 py-1 bg-amber-500/20 text-amber-500 text-xs font-bold uppercase tracking-wider inline-block">
                  Elite Exclusive
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="bg-gray-900 border-gray-800 opacity-60">
              <CardContent className="py-8 text-center relative">
                <div className="absolute inset-0 flex items-center justify-center bg-black/60 z-10">
                  <div className="text-center">
                    <Lock className="w-8 h-8 text-gray-500 mx-auto mb-2" />
                    <p className="text-gray-400 text-sm">Elite Pivot Only</p>
                  </div>
                </div>
                <div className="w-16 h-16 rounded-full bg-gray-800 flex items-center justify-center mx-auto mb-4">
                  <Calendar className="w-8 h-8 text-gray-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-600 mb-2">1-on-1 Strategy Calls</h3>
                <p className="text-gray-600 text-sm">Upgrade to Elite Pivot</p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Curriculum Grid */}
        <div className="mb-12">
          <h2 className="text-2xl font-black text-white mb-6 flex items-center gap-3">
            <span className="w-1 h-8 bg-orange-600"></span>
            Your Curriculum
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {modules.map((module, idx) => (
              <Card 
                key={idx}
                className={`bg-gray-900 border-gray-800 relative overflow-hidden ${
                  module.status === 'locked' ? 'opacity-70' : ''
                }`}
              >
                {module.status === 'locked' && (
                  <div className="absolute top-4 right-4">
                    <Lock className="w-5 h-5 text-gray-600" />
                  </div>
                )}
                
                <CardHeader className="pb-2">
                  <div className="text-orange-600 text-sm font-bold uppercase tracking-wider">
                    Week {module.week}
                  </div>
                  <CardTitle className="text-xl font-bold text-white">
                    {module.title}
                  </CardTitle>
                </CardHeader>
                
                <CardContent>
                  <p className="text-gray-400 text-sm mb-4">{module.description}</p>
                  
                  {module.status === 'locked' ? (
                    <div className="text-xs text-gray-500 flex items-center gap-2">
                      <Lock className="w-3 h-3" />
                      Unlocks {module.unlock_date}
                    </div>
                  ) : (
                    <Link to="/academy/lms">
                      <Button className="w-full bg-orange-600 hover:bg-orange-700 text-white text-sm">
                        Start Module
                      </Button>
                    </Link>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Tier Benefits */}
        <Card className="bg-gray-900 border-gray-800 mb-8">
          <CardHeader>
            <CardTitle className="text-xl font-bold text-white flex items-center gap-3">
              <TierIcon className="w-6 h-6" style={{ color: tierConfig.color }} />
              Your {tierConfig.name} Benefits
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="grid md:grid-cols-2 gap-4 mb-6">
              {tier_info.features?.map((feature, idx) => (
                <li key={idx} className="flex items-center gap-3 text-gray-300">
                  <div 
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: tierConfig.color }}
                  />
                  {feature}
                </li>
              ))}
            </ul>
            
            {/* Gear Kit Status - Show for Major and Elite */}
            {(student.subscription_tier === 'academy_major' || student.subscription_tier === 'elite_pivot') && (
              <div className="border-t border-gray-800 pt-4 mt-4">
                <div className="flex items-center justify-between p-4 bg-gray-800/50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-orange-600/20 flex items-center justify-center">
                      <svg className="w-5 h-5 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                      </svg>
                    </div>
                    <div>
                      <div className="text-white font-bold">
                        {student.subscription_tier === 'elite_pivot' ? 'Full Academy Gear Kit' : 'PRSVR Academy T-Shirt'}
                      </div>
                      <div className="text-gray-500 text-sm">Gear Status</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center gap-2 text-yellow-500 text-sm font-bold">
                      <div className="w-2 h-2 rounded-full bg-yellow-500 animate-pulse"></div>
                      Processing
                    </div>
                    <div className="text-gray-500 text-xs">Ships Jan 15th</div>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Back to Academy Link */}
        <div className="text-center">
          <Link 
            to="/academy"
            className="text-gray-500 hover:text-orange-600 transition-colors text-sm"
          >
            ← Back to Academy Home
          </Link>
        </div>
      </div>

      {/* Scheduler Modal for Elite */}
      <Dialog open={showScheduler} onOpenChange={setShowScheduler}>
        <DialogContent className="bg-gray-900 border-gray-800 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white text-2xl font-black flex items-center gap-3">
              <Calendar className="w-6 h-6 text-amber-500" />
              Schedule Your <span className="text-amber-500">1-on-1 Call</span>
            </DialogTitle>
          </DialogHeader>
          
          <div className="mt-4">
            <p className="text-gray-400 text-sm mb-6">
              Book your weekly strategy session with your dedicated performance coach.
            </p>
            
            <ConsultationScheduler 
              athleteName={student.name}
              readinessZone={student.readiness_zone}
              onBookingComplete={() => {
                setShowScheduler(false);
                toast.success('Strategy call booked!');
              }}
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Welcome Film Modal */}
      <Dialog open={showWelcomeFilm} onOpenChange={setShowWelcomeFilm}>
        <DialogContent className="bg-gray-900 border-gray-800 max-w-3xl">
          <DialogHeader>
            <DialogTitle className="text-white text-2xl font-black flex items-center gap-3">
              <Play className="w-6 h-6 text-orange-600" />
              Welcome to <span className="text-orange-600">PRSVR Academy</span>
            </DialogTitle>
          </DialogHeader>
          
          <div className="mt-4">
            <div className="aspect-video bg-black rounded-lg overflow-hidden mb-4">
              {/* Placeholder video - replace with actual welcome video */}
              <iframe
                className="w-full h-full"
                src="https://www.youtube.com/embed/dQw4w9WgXcQ?autoplay=0"
                title="Welcome to PRSVR Academy"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
            
            <div className="text-center">
              <h3 className="text-xl font-bold text-white mb-2">Congratulations, {student.name}!</h3>
              <p className="text-gray-400 mb-4">
                You&apos;ve officially enrolled in the <span className="text-orange-600 font-bold">Pace & Pivot</span> course at Scholar Athlete Academy. The first move is the pivot.
              </p>
              <Button 
                onClick={() => {
                  setShowWelcomeFilm(false);
                  updateOnboardingProgress('welcome_film', true);
                }}
                className="bg-orange-600 hover:bg-orange-700 text-white font-bold"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Mark as Watched
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AcademyDashboard;
